import { useMemo, useCallback, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useCalendar } from '../context/CalendarContext';
import { useCalendarNavigation } from '../context/CalendarNavigationContext';
import { generateTimeSlots, getCurrentTimePosition } from '../utils/timeHelpers';
import { calculateGridHeight } from '../utils/eventPositioning';
import { CALENDAR_CONSTANTS } from '../utils/constants';

export const useCalendarTimeGrid = () => {
  const { timeRange, hourHeight } = useCalendar();
  const { scrollTargetTime, clearHighlight } = useCalendarNavigation();
  const scrollContainerRef = useRef(null);

  const { startHour, endHour } = timeRange;

  const timeSlots = useMemo(() => {
    return generateTimeSlots(startHour, endHour, 60);
  }, [startHour, endHour]);

  const gridHeight = useMemo(() => {
    return calculateGridHeight(startHour, endHour, hourHeight);
  }, [startHour, endHour, hourHeight]);

  const gridLines = useMemo(() => {
    return timeSlots.map((slot, index) => ({
      key: slot.time,
      top: CALENDAR_CONSTANTS.HEADER_HEIGHT + index * hourHeight,
    }));
  }, [timeSlots, hourHeight]);

  const currentTimePosition = useMemo(() => {
    return getCurrentTimePosition(startHour, hourHeight);
  }, [startHour, hourHeight]);

  const scrollToTime = useCallback((time, behavior = 'smooth') => {
    if (!scrollContainerRef.current) return;

    const [hours, minutes] = time.split(':').map(Number);
    const targetPosition = (hours - startHour) * hourHeight + (minutes / 60) * hourHeight;
    const viewportHeight = scrollContainerRef.current.clientHeight;
    const scrollTo = Math.max(0, targetPosition - viewportHeight / 3 + CALENDAR_CONSTANTS.HEADER_HEIGHT);

    scrollContainerRef.current.scrollTo({
      top: scrollTo,
      behavior,
    });
  }, [startHour, hourHeight]);

  const scrollToCurrentTime = useCallback((behavior = 'smooth') => {
    const now = dayjs();
    const currentTime = now.format('HH:mm');
    scrollToTime(currentTime, behavior);
  }, [scrollToTime]);

  useEffect(() => {
    if (scrollTargetTime && scrollContainerRef.current) {
      scrollToTime(scrollTargetTime);

      const timer = setTimeout(() => {
        clearHighlight();
      }, CALENDAR_CONSTANTS.HIGHLIGHT_DURATION_MS);

      return () => clearTimeout(timer);
    }
  }, [scrollTargetTime, scrollToTime, clearHighlight]);

  const getTimeAtPosition = useCallback((yPosition) => {
    const relativePosition = yPosition - CALENDAR_CONSTANTS.HEADER_HEIGHT;
    const totalMinutes = (relativePosition / hourHeight) * 60;
    const hours = Math.floor(totalMinutes / 60) + startHour;
    const minutes = Math.round(totalMinutes % 60);

    return {
      hours: Math.max(startHour, Math.min(hours, endHour - 1)),
      minutes: Math.max(0, Math.min(minutes, 59)),
      formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
    };
  }, [startHour, endHour, hourHeight]);

  const getPositionForTime = useCallback((time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return CALENDAR_CONSTANTS.HEADER_HEIGHT + ((hours - startHour) + minutes / 60) * hourHeight;
  }, [startHour, hourHeight]);

  return {
    scrollContainerRef,
    timeSlots,
    gridHeight,
    gridLines,
    currentTimePosition,
    startHour,
    endHour,
    hourHeight,

    scrollToTime,
    scrollToCurrentTime,
    getTimeAtPosition,
    getPositionForTime,
  };
};

export default useCalendarTimeGrid;
